/* Xiaomi 2026 · DBR (Distributor) Directory
 *
 * First-class entity for the distributors who physically receive gift
 * consignments and hand them over to dealers. Inspired by Dhiraj's PRD.
 *
 * - List all DBRs · search · zone filter
 * - Inline create / edit
 * - Per-DBR drill: address (used by Kavita's gift-ops Ship-to selector),
 *   contact, and a list of dealers under this DBR.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, X, Phone, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const EMPTY = {
  name: '', legal_name: '', zone: '', states: [],
  contact_name: '', contact_phone: '', contact_email: '',
  address_line1: '', address_line2: '', city: '', state: '', pincode: '',
  gst_number: '', notes: '', active: true,
};


export default function XiaomiDBR() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [zone, setZone] = useState('');
  const [edit, setEdit] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (zone) params.set('zone', zone);
    try {
      const r = await axios.get(`${API}/xiaomi/dbrs?${params.toString()}`, authHdr());
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error('Failed to load DBRs');
    }
  };

  useEffect(() => { load(); }, [q, zone]);

  return (
    <div className="space-y-5" data-testid="xiaomi-dbr-page">
      <div className="flex justify-between items-end gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}>
            DBRs · <span style={{ color: '#ff6900' }}>distributors</span>
          </h1>
          <p className="text-sm text-soft mt-1">
            {rows.length} DBR{rows.length === 1 ? '' : 's'} on record · physical handover network for Xiaomi gift ops.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            placeholder="Search name / city / state…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="px-3 py-2 border border-line rounded-lg text-sm"
            data-testid="dbr-search"
          />
          <select value={zone} onChange={(e) => setZone(e.target.value)}
            className="px-3 py-2 border border-line rounded-lg text-sm bg-white"
            data-testid="dbr-zone-filter"
          >
            <option value="">All zones</option>
            <option value="North">North</option>
            <option value="South">South</option>
            <option value="East">East</option>
            <option value="West">West</option>
          </select>
          <button
            onClick={() => setEdit({ ...EMPTY, _new: true })}
            className="px-3 py-2 rounded-lg text-sm font-semibold text-white flex items-center gap-1"
            style={{ background: '#ff6900' }}
            data-testid="dbr-create-btn"
          >
            <Plus size={14} /> Add DBR
          </button>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-line rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead style={{ background: '#f3ebd9' }}>
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-soft">Name</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-soft">Zone</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-soft">City</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-soft">Contact</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-soft">Dealers</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center italic text-soft p-8">
                No DBRs yet. Click <strong>Add DBR</strong> to register your first distributor.
              </td></tr>
            )}
            {rows.map((d) => (
              <tr key={d._id} className="border-t border-line hover:bg-[#fffbf0]">
                <td className="px-3 py-2.5 font-medium">{d.name}</td>
                <td className="px-3 py-2.5 text-soft">{d.zone || '—'}</td>
                <td className="px-3 py-2.5 text-soft">{[d.city, d.state].filter(Boolean).join(', ') || '—'}</td>
                <td className="px-3 py-2.5 text-soft">
                  {d.contact_name && <div>{d.contact_name}</div>}
                  {d.contact_phone && <div className="text-[11px] font-mono">{d.contact_phone}</div>}
                </td>
                <td className="px-3 py-2.5 text-right font-semibold">{d.dealer_count || 0}</td>
                <td className="px-3 py-2.5 text-right">
                  <button onClick={() => setEdit({ ...d })}
                    className="p-1.5 rounded hover:bg-[#fef3c7]" style={{ color: '#9A4B0E' }}
                    data-testid={`dbr-edit-${d._id}`}>
                    <Pencil size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <DBREditModal
          dbr={edit}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); load(); }}
        />
      )}
    </div>
  );
}


function DBREditModal({ dbr, onClose, onSaved }) {
  const [draft, setDraft] = useState(dbr);
  const [busy, setBusy] = useState(false);
  const isNew = !!dbr._new;

  const save = async () => {
    if (!draft.name?.trim()) {
      toast.error('Name required'); return;
    }
    setBusy(true);
    try {
      const payload = { ...draft }; delete payload._new; delete payload._id; delete payload.dealer_count;
      if (isNew) {
        await axios.post(`${API}/xiaomi/dbrs`, payload, authHdr());
        toast.success('DBR created');
      } else {
        await axios.patch(`${API}/xiaomi/dbrs/${encodeURIComponent(dbr._id)}`, payload, authHdr());
        toast.success('DBR updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const F = (key, props = {}) => (
    <input
      value={draft[key] || ''}
      onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      className="w-full px-3 py-1.5 border border-line rounded text-sm"
      {...props}
    />
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="dbr-edit-modal"
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b sticky top-0 bg-white flex justify-between items-start" style={{ borderColor: '#e6dccd' }}>
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>
            {isNew ? 'Add DBR' : `Edit · ${dbr.name}`}
          </h2>
          <button onClick={onClose} className="text-soft hover:text-ink" data-testid="dbr-edit-close">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <FieldRow label="Display name *">{F('name', { 'data-testid': 'dbr-edit-name' })}</FieldRow>
          <FieldRow label="Legal name">{F('legal_name')}</FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Zone">
              <select
                value={draft.zone || ''}
                onChange={(e) => setDraft({ ...draft, zone: e.target.value })}
                className="w-full px-3 py-1.5 border border-line rounded text-sm bg-white"
                data-testid="dbr-edit-zone"
              >
                <option value="">— select —</option>
                <option>North</option><option>South</option><option>East</option><option>West</option>
              </select>
            </FieldRow>
            <FieldRow label="States covered (comma-sep)">
              <input
                value={(draft.states || []).join(', ')}
                onChange={(e) => setDraft({ ...draft, states: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                className="w-full px-3 py-1.5 border border-line rounded text-sm"
              />
            </FieldRow>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FieldRow label="Contact name">{F('contact_name')}</FieldRow>
            <FieldRow label="Contact phone"><div className="flex items-center gap-1"><Phone size={12} className="text-soft" />{F('contact_phone')}</div></FieldRow>
            <FieldRow label="Contact email"><div className="flex items-center gap-1"><Mail size={12} className="text-soft" />{F('contact_email')}</div></FieldRow>
          </div>
          <FieldRow label="Address (line 1) · used as ship-to in Kavita's gift-ops">{F('address_line1', { 'data-testid': 'dbr-edit-address-1' })}</FieldRow>
          <FieldRow label="Address (line 2)">{F('address_line2')}</FieldRow>
          <div className="grid grid-cols-3 gap-3">
            <FieldRow label="City">{F('city')}</FieldRow>
            <FieldRow label="State">{F('state')}</FieldRow>
            <FieldRow label="Pincode">{F('pincode')}</FieldRow>
          </div>
          <FieldRow label="GST number">{F('gst_number')}</FieldRow>
          <FieldRow label="Notes">
            <textarea
              value={draft.notes || ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-1.5 border border-line rounded text-sm"
            />
          </FieldRow>
        </div>
        <div className="p-4 border-t sticky bottom-0 bg-white flex gap-2 justify-end" style={{ borderColor: '#e6dccd' }}>
          <button onClick={onClose} disabled={busy}
            className="px-3 py-1.5 rounded text-sm border bg-white">Cancel</button>
          <button onClick={save} disabled={busy}
            className="px-4 py-1.5 rounded text-sm text-white font-semibold disabled:opacity-50"
            style={{ background: '#16a34a' }}
            data-testid="dbr-edit-save">
            {busy ? 'Saving…' : isNew ? 'Create' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}


function FieldRow({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider text-soft mb-1">{label}</div>
      {children}
    </label>
  );
}
